import type { WorkItemDetailBundlePayload } from '@/components/work-items/detailBundleHydration';
import type { ExportRecord } from '@/lib/export/clientExport';

const APPOINTMENT_FETCH_CHUNK_SIZE = 100;

type LooseRecord = Record<string, unknown>;

type RelationshipRow = {
  source_item_id?: string;
  target_item_id?: string;
  relationship_type?: string;
};

function isRecord(value: unknown): value is LooseRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asNonEmptyString(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === 'number' || typeof value === 'bigint') {
    return String(value);
  }

  return null;
}

function asFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function firstNonEmpty(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

function uniqueStrings(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];

  values.forEach((value) => {
    if (!value || seen.has(value)) {
      return;
    }

    seen.add(value);
    normalized.push(value);
  });

  return normalized;
}

function formatDateTime(value: unknown): string | null {
  const raw = asNonEmptyString(value);
  if (!raw) {
    return null;
  }

  const timestamp = new Date(raw);
  if (Number.isNaN(timestamp.getTime())) {
    return raw;
  }

  return new Intl.DateTimeFormat('en-ZA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(timestamp);
}

function formatDateTimeRange(start: unknown, end: unknown): string | null {
  const formattedStart = formatDateTime(start);
  const formattedEnd = formatDateTime(end);

  if (formattedStart && formattedEnd) {
    return `${formattedStart} → ${formattedEnd}`;
  }

  return formattedStart || formattedEnd || null;
}

function formatHours(value: unknown): string | null {
  const hours = asFiniteNumber(value);
  if (hours == null) {
    return null;
  }

  return `${hours}h`;
}

function formatKilometers(value: unknown): string | null {
  const km = asFiniteNumber(value);
  if (km == null) {
    return null;
  }

  return `${km} km`;
}

function normalizeRecordArray(value: unknown): LooseRecord[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord);
}

function summarizeAssigneeArray(value: unknown): string | null {
  const assignees = normalizeRecordArray(value);
  if (assignees.length === 0) {
    return null;
  }

  const names = assignees
    .map((assignee) => {
      const fullName = [
        asNonEmptyString(assignee.first_name),
        asNonEmptyString(assignee.last_name),
      ].filter(Boolean).join(' ').trim();

      return firstNonEmpty(
        fullName || null,
        asNonEmptyString(assignee.name),
        asNonEmptyString(assignee.display_name),
        asNonEmptyString(assignee.email),
      );
    })
    .filter((name): name is string => Boolean(name));

  return names.length > 0 ? uniqueStrings(names).join(', ') : null;
}

function summarizeWorkItemAssignees(workItem: LooseRecord): string | null {
  return firstNonEmpty(
    asNonEmptyString(workItem.assigned_to_name),
    asNonEmptyString(workItem.assignee_name),
    summarizeAssigneeArray(workItem.assigned_users_details),
    summarizeAssigneeArray(workItem.assignees),
  );
}

function buildFetchErrorMessage(response: Response, fallbackMessage: string, payload?: any): string {
  const payloadError = typeof payload?.error === 'string' && payload.error.trim()
    ? payload.error.trim()
    : null;

  if (payloadError) {
    return payloadError;
  }

  return `${fallbackMessage} (${response.status}${response.statusText ? ` ${response.statusText}` : ''})`;
}

function chunkArray<T>(items: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];

  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }

  return chunks;
}

function buildPurchaseItems(items: unknown): Array<Record<string, unknown>> {
  return normalizeRecordArray(items).map((item) => ({
    name: firstNonEmpty(
      asNonEmptyString(item.name),
      asNonEmptyString(item.title),
      asNonEmptyString(item.item_name),
      asNonEmptyString(item.description),
    ),
    sku: firstNonEmpty(asNonEmptyString(item.sku), asNonEmptyString(item.code)),
    quantity: asFiniteNumber(item.quantity ?? item.qty),
    unit: asNonEmptyString(item.unit),
    notes: firstNonEmpty(asNonEmptyString(item.notes), asNonEmptyString(item.note)),
  }));
}

function buildProgressChildren(progressChildren: unknown): Array<Record<string, unknown>> {
  return normalizeRecordArray(progressChildren).map((child) => ({
    id: asNonEmptyString(child.id),
    item_number: asNonEmptyString(child.item_number),
    item_type: asNonEmptyString(child.item_type),
    title: asNonEmptyString(child.title),
    status: asNonEmptyString(child.status),
    progress_percentage: asFiniteNumber(child.progress_percentage),
  }));
}

function buildLinkedWorkItems(items: unknown): Array<Record<string, unknown>> {
  return normalizeRecordArray(items).map((item) => ({
    id: asNonEmptyString(item.id),
    item_number: asNonEmptyString(item.item_number),
    title: asNonEmptyString(item.title),
    status: asNonEmptyString(item.status),
    scheduled_start: asNonEmptyString(item.scheduled_start),
    scheduled_end: asNonEmptyString(item.scheduled_end),
    relationship_id: asNonEmptyString(item.relationship_id),
  }));
}

function buildSectionLines(title: string, fields: Array<[string, string | null | undefined]>): string[] {
  const presentFields = fields.filter(([, value]) => typeof value === 'string' && value.trim().length > 0);
  if (presentFields.length === 0) {
    return [];
  }

  return [
    `${title}:`,
    ...presentFields.map(([label, value]) => `- ${label}: ${value}`),
  ];
}

function stringifyUnknown(value: unknown): string | null {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string') {
    return value.trim() || null;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function normalizeChildAppointmentExportRecord(item: LooseRecord, relationshipType?: string | null): ExportRecord {
  return {
    id: asNonEmptyString(item.id),
    item_number: asNonEmptyString(item.item_number),
    title: asNonEmptyString(item.title),
    description: asNonEmptyString(item.description),
    status: asNonEmptyString(item.status),
    priority: asNonEmptyString(item.priority),
    scheduled_start: asNonEmptyString(item.scheduled_start),
    scheduled_end: asNonEmptyString(item.scheduled_end),
    assigned_to_name: summarizeWorkItemAssignees(item),
    contact_name: asNonEmptyString(item.contact_name),
    location_name: asNonEmptyString(item.location_name),
    location_address: asNonEmptyString(item.location_address),
    relationship_type: relationshipType || null,
  };
}

function buildChildAppointmentColumns(appointments: ExportRecord[]) {
  const appointmentNumbers = appointments
    .map((appointment) => asNonEmptyString(appointment.item_number))
    .filter((value): value is string => Boolean(value));
  const appointmentTitles = appointments
    .map((appointment) => asNonEmptyString(appointment.title))
    .filter((value): value is string => Boolean(value));
  const appointmentStatuses = appointments
    .map((appointment) => asNonEmptyString(appointment.status))
    .filter((value): value is string => Boolean(value));
  const appointmentSchedule = appointments
    .map((appointment) => {
      const itemNumber = asNonEmptyString(appointment.item_number) || asNonEmptyString(appointment.id) || 'Appointment';
      const schedule = formatDateTimeRange(appointment.scheduled_start, appointment.scheduled_end);
      const status = asNonEmptyString(appointment.status);
      return firstNonEmpty(
        schedule ? `${itemNumber}: ${schedule}` : null,
        status ? `${itemNumber}: ${status}` : null,
        itemNumber,
      );
    })
    .filter((value): value is string => Boolean(value));

  return {
    child_appointment_count: appointments.length,
    child_appointment_numbers: appointmentNumbers.join(', '),
    child_appointment_titles: appointmentTitles.join(' | '),
    child_appointment_statuses: appointmentStatuses.join(', '),
    child_appointment_schedule: appointmentSchedule.join(' | '),
    child_appointments: appointments.length > 0 ? appointments : '',
  };
}

export async function fetchWorkItemDetailBundle(
  workItemId: string,
  accessToken: string,
): Promise<WorkItemDetailBundlePayload> {
  const response = await fetch(`/api/v1/work-items/${workItemId}/detail-bundle`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok || !payload?.success || !isRecord(payload.data)) {
    throw new Error(buildFetchErrorMessage(response, 'Failed to load work item details', payload));
  }

  return payload.data as WorkItemDetailBundlePayload;
}

export function buildWorkItemDetailCopyPayload(bundle: WorkItemDetailBundlePayload): ExportRecord {
  const workItem = isRecord(bundle.work_item) ? bundle.work_item : {};
  const overview = isRecord(bundle.overview) ? bundle.overview : {};
  const sidebar = isRecord(bundle.sidebar) ? bundle.sidebar : {};
  const contactDetails = isRecord(overview.contact_details) ? overview.contact_details : {};
  const company = isRecord(contactDetails.company) ? contactDetails.company : {};
  const location = isRecord(contactDetails.location) ? contactDetails.location : {};
  const contactPeople = normalizeRecordArray(overview.contact_people);
  const primaryContactPerson = contactPeople[0] || {};
  const customFields = isRecord(workItem.custom_fields) ? workItem.custom_fields : {};
  const timeSummary = isRecord(overview.time_summary) ? overview.time_summary : {};

  const resolvedCompanyName = firstNonEmpty(
    asNonEmptyString(company.company_name),
    asNonEmptyString(company.display_name),
    asNonEmptyString(company.contact_person),
    asNonEmptyString(workItem.contact_name),
  );
  const resolvedPersonName = firstNonEmpty(
    [asNonEmptyString(primaryContactPerson.first_name), asNonEmptyString(primaryContactPerson.last_name)].filter(Boolean).join(' ').trim() || null,
    asNonEmptyString(primaryContactPerson.name),
    asNonEmptyString(location.contact_person),
    asNonEmptyString(workItem.contact_person),
  );
  const resolvedPersonRole = firstNonEmpty(
    asNonEmptyString(primaryContactPerson.job_title),
    asNonEmptyString(primaryContactPerson.role),
    asNonEmptyString(primaryContactPerson.position),
  );
  const resolvedLocationName = firstNonEmpty(
    asNonEmptyString(location.location_name),
    asNonEmptyString(workItem.location_name),
  );
  const resolvedLocationAddress = firstNonEmpty(
    asNonEmptyString(location.address),
    asNonEmptyString(workItem.location_address),
  );
  const resolvedLocationContactName = firstNonEmpty(
    asNonEmptyString(location.contact_person),
    resolvedPersonName,
  );
  const resolvedLocationContactPhone = asNonEmptyString(location.contact_phone);
  const resolvedLocationContactEmail = asNonEmptyString(location.contact_email);
  const primaryPhone = firstNonEmpty(
    asNonEmptyString(primaryContactPerson.mobile),
    asNonEmptyString(primaryContactPerson.phone),
    resolvedLocationContactPhone,
    asNonEmptyString(company.mobile),
    asNonEmptyString(company.phone),
    asNonEmptyString(workItem.contact_phone),
  );
  const primaryEmail = firstNonEmpty(
    asNonEmptyString(primaryContactPerson.email),
    resolvedLocationContactEmail,
    asNonEmptyString(company.email),
    asNonEmptyString(workItem.contact_email),
  );
  const assignedToName = summarizeWorkItemAssignees(workItem);
  const notes = firstNonEmpty(
    asNonEmptyString(workItem.notes),
    asNonEmptyString(customFields.additional_notes),
    asNonEmptyString(customFields.notes),
  );

  return {
    item: {
      id: asNonEmptyString(workItem.id),
      item_number: asNonEmptyString(workItem.item_number),
      item_type: asNonEmptyString(workItem.item_type),
      item_subtype: asNonEmptyString(workItem.item_subtype),
      module_context: asNonEmptyString(workItem.module_context),
      title: asNonEmptyString(workItem.title),
      description: asNonEmptyString(workItem.description),
      status: asNonEmptyString(workItem.status),
      priority: asNonEmptyString(workItem.priority),
      assigned_to_name: assignedToName,
      progress_percentage: asFiniteNumber(workItem.progress_percentage),
      estimated_hours: asFiniteNumber(workItem.estimated_hours),
      actual_hours: asFiniteNumber(workItem.actual_hours),
      start_date: asNonEmptyString(workItem.start_date),
      due_date: asNonEmptyString(workItem.due_date),
      scheduled_start: asNonEmptyString(workItem.scheduled_start),
      scheduled_end: asNonEmptyString(workItem.scheduled_end),
      created_at: asNonEmptyString(workItem.created_at),
      updated_at: asNonEmptyString(workItem.updated_at),
    },
    contact: {
      company_name: resolvedCompanyName,
      person_name: resolvedPersonName,
      person_role: resolvedPersonRole,
      phone: primaryPhone,
      email: primaryEmail,
    },
    location: {
      name: resolvedLocationName,
      address: resolvedLocationAddress,
      contact_name: resolvedLocationContactName,
      contact_phone: resolvedLocationContactPhone,
      contact_email: resolvedLocationContactEmail,
    },
    timing: {
      total_hours: asFiniteNumber(timeSummary.total_hours),
      child_appointment_count: asFiniteNumber(timeSummary.child_appointment_count),
      includes_related: timeSummary.includes_related === true,
      travel_minutes: asFiniteNumber(timeSummary.travel_minutes),
      total_distance_km: asFiniteNumber(timeSummary.total_distance_km),
    },
    notes,
    purchase_items: buildPurchaseItems(customFields.purchase_items),
    linked_appointments: buildLinkedWorkItems(sidebar.linked_appointments),
    linked_jobs: buildLinkedWorkItems(sidebar.linked_jobs),
    progress_children: buildProgressChildren(overview.progress_children),
    custom_fields: customFields,
  };
}

export function buildWorkItemDetailCopyText(bundle: WorkItemDetailBundlePayload): string {
  const payload = buildWorkItemDetailCopyPayload(bundle);
  const item = isRecord(payload.item) ? payload.item : {};
  const contact = isRecord(payload.contact) ? payload.contact : {};
  const location = isRecord(payload.location) ? payload.location : {};
  const timing = isRecord(payload.timing) ? payload.timing : {};
  const purchaseItems = Array.isArray(payload.purchase_items) ? payload.purchase_items : [];
  const linkedAppointments = Array.isArray(payload.linked_appointments) ? payload.linked_appointments : [];
  const linkedJobs = Array.isArray(payload.linked_jobs) ? payload.linked_jobs : [];
  const progressChildren = Array.isArray(payload.progress_children) ? payload.progress_children : [];
  const customFieldsText = stringifyUnknown(payload.custom_fields);

  const sections: string[][] = [];

  sections.push(buildSectionLines('Work item', [
    ['Number', asNonEmptyString(item.item_number)],
    ['Title', asNonEmptyString(item.title)],
    ['Type', firstNonEmpty(asNonEmptyString(item.item_type), asNonEmptyString(item.item_subtype))],
    ['Subtype', asNonEmptyString(item.item_subtype)],
    ['Module', asNonEmptyString(item.module_context)],
    ['Status', asNonEmptyString(item.status)],
    ['Priority', asNonEmptyString(item.priority)],
    ['Assigned to', asNonEmptyString(item.assigned_to_name)],
    ['Progress', asFiniteNumber(item.progress_percentage) != null ? `${asFiniteNumber(item.progress_percentage)}%` : null],
    ['Estimated hours', formatHours(item.estimated_hours)],
    ['Actual hours', formatHours(item.actual_hours)],
    ['Start date', formatDateTime(item.start_date)],
    ['Due date', formatDateTime(item.due_date)],
    ['Scheduled', formatDateTimeRange(item.scheduled_start, item.scheduled_end)],
    ['Created', formatDateTime(item.created_at)],
    ['Updated', formatDateTime(item.updated_at)],
    ['Description', asNonEmptyString(item.description)],
  ]));

  sections.push(buildSectionLines('Contact', [
    ['Company', asNonEmptyString(contact.company_name)],
    ['Person', asNonEmptyString(contact.person_name)],
    ['Role', asNonEmptyString(contact.person_role)],
    ['Phone', asNonEmptyString(contact.phone)],
    ['Email', asNonEmptyString(contact.email)],
  ]));

  sections.push(buildSectionLines('Location', [
    ['Name', asNonEmptyString(location.name)],
    ['Address', asNonEmptyString(location.address)],
    ['Contact', asNonEmptyString(location.contact_name)],
    ['Contact phone', asNonEmptyString(location.contact_phone)],
    ['Contact email', asNonEmptyString(location.contact_email)],
  ]));

  sections.push(buildSectionLines('Time & travel', [
    ['Total hours', formatHours(timing.total_hours)],
    ['Child appointments', asFiniteNumber(timing.child_appointment_count) != null ? String(asFiniteNumber(timing.child_appointment_count)) : null],
    ['Time source', timing.includes_related === true ? 'Includes child appointments' : null],
    ['Travel minutes', asFiniteNumber(timing.travel_minutes) != null ? `${asFiniteNumber(timing.travel_minutes)} min` : null],
    ['Distance', formatKilometers(timing.total_distance_km)],
  ]));

  if (typeof payload.notes === 'string' && payload.notes.trim()) {
    sections.push(['Notes:', payload.notes.trim()]);
  }

  if (purchaseItems.length > 0) {
    sections.push([
      'Purchase items:',
      ...purchaseItems.map((item) => {
        const record = isRecord(item) ? item : {};
        const parts = [
          asNonEmptyString(record.name) || 'Item',
          asFiniteNumber(record.quantity) != null ? `qty ${asFiniteNumber(record.quantity)}` : null,
          asNonEmptyString(record.unit),
          asNonEmptyString(record.sku),
          asNonEmptyString(record.notes),
        ].filter((value): value is string => Boolean(value));

        return `- ${parts.join(' | ')}`;
      }),
    ]);
  }

  if (linkedAppointments.length > 0) {
    sections.push([
      'Linked appointments:',
      ...linkedAppointments.map((appointment) => {
        const record = isRecord(appointment) ? appointment : {};
        const parts = [
          asNonEmptyString(record.item_number) || 'Appointment',
          asNonEmptyString(record.title),
          asNonEmptyString(record.status),
          formatDateTimeRange(record.scheduled_start, record.scheduled_end),
        ].filter((value): value is string => Boolean(value));

        return `- ${parts.join(' | ')}`;
      }),
    ]);
  }

  if (linkedJobs.length > 0) {
    sections.push([
      'Linked jobs:',
      ...linkedJobs.map((job) => {
        const record = isRecord(job) ? job : {};
        const parts = [
          asNonEmptyString(record.item_number) || 'Job',
          asNonEmptyString(record.title),
          asNonEmptyString(record.status),
          formatDateTime(record.scheduled_start),
        ].filter((value): value is string => Boolean(value));

        return `- ${parts.join(' | ')}`;
      }),
    ]);
  }

  if (progressChildren.length > 0) {
    sections.push([
      'Progress children:',
      ...progressChildren.map((child) => {
        const record = isRecord(child) ? child : {};
        const parts = [
          firstNonEmpty(asNonEmptyString(record.item_number), asNonEmptyString(record.title), 'Child item'),
          asNonEmptyString(record.status),
          asFiniteNumber(record.progress_percentage) != null ? `${asFiniteNumber(record.progress_percentage)}%` : null,
        ].filter((value): value is string => Boolean(value));

        return `- ${parts.join(' | ')}`;
      }),
    ]);
  }

  if (customFieldsText && customFieldsText !== '{}' && customFieldsText !== '[]') {
    sections.push([
      'Custom fields:',
      customFieldsText,
    ]);
  }

  return sections
    .filter((section) => section.length > 0)
    .map((section) => section.join('\n'))
    .join('\n\n');
}

export async function enrichJobExportRecordsWithChildAppointments(
  records: ExportRecord[],
  accessToken: string,
): Promise<ExportRecord[]> {
  const jobIds = uniqueStrings(
    records
      .filter((record) => asNonEmptyString(record.item_type) === 'job')
      .map((record) => asNonEmptyString(record.id)),
  );

  if (jobIds.length === 0) {
    return records;
  }

  const relationshipsResponse = await fetch('/api/v1/work-item-relationships', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      action: 'getByParentIds',
      parentIds: jobIds,
      relationshipType: 'child_of',
    }),
  });

  const relationshipsPayload = await relationshipsResponse.json().catch(() => null);
  if (!relationshipsResponse.ok || !relationshipsPayload?.success) {
    throw new Error(buildFetchErrorMessage(relationshipsResponse, 'Failed to load child appointment relationships', relationshipsPayload));
  }

  const relationships = Array.isArray(relationshipsPayload.data)
    ? (relationshipsPayload.data as RelationshipRow[])
    : [];

  const appointmentIds = uniqueStrings(
    relationships.map((relationship) => asNonEmptyString(relationship.source_item_id)),
  );

  const appointmentsById = new Map<string, LooseRecord>();

  for (const appointmentIdChunk of chunkArray(appointmentIds, APPOINTMENT_FETCH_CHUNK_SIZE)) {
    if (appointmentIdChunk.length === 0) {
      continue;
    }

    const params = new URLSearchParams();
    params.set('ids', appointmentIdChunk.join(','));
    params.set('item_type', 'appointment');
    params.set('limit', String(Math.min(200, Math.max(appointmentIdChunk.length, 1))));
    params.set('include_total', 'false');

    const appointmentResponse = await fetch(`/api/v1/work-items?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const appointmentPayload = await appointmentResponse.json().catch(() => null);
    if (!appointmentResponse.ok || !appointmentPayload?.success) {
      throw new Error(buildFetchErrorMessage(appointmentResponse, 'Failed to load child appointment rows', appointmentPayload));
    }

    const appointmentRows: LooseRecord[] = Array.isArray(appointmentPayload.data)
      ? appointmentPayload.data.filter(isRecord)
      : [];

    appointmentRows.forEach((appointment: LooseRecord) => {
      const appointmentId = asNonEmptyString(appointment.id);
      if (appointmentId) {
        appointmentsById.set(appointmentId, appointment);
      }
    });
  }

  const appointmentsByJobId = new Map<string, ExportRecord[]>();
  relationships.forEach((relationship) => {
    const jobId = asNonEmptyString(relationship.target_item_id);
    const appointmentId = asNonEmptyString(relationship.source_item_id);

    if (!jobId || !appointmentId) {
      return;
    }

    const appointment = appointmentsById.get(appointmentId);
    if (!appointment) {
      return;
    }

    const normalizedAppointment = normalizeChildAppointmentExportRecord(
      appointment,
      asNonEmptyString(relationship.relationship_type),
    );
    const existingAppointments = appointmentsByJobId.get(jobId) || [];
    appointmentsByJobId.set(jobId, [...existingAppointments, normalizedAppointment]);
  });

  return records.map((record) => {
    const jobId = asNonEmptyString(record.id);
    const appointments = jobId ? appointmentsByJobId.get(jobId) || [] : [];

    return {
      ...record,
      ...buildChildAppointmentColumns(appointments),
    };
  });
}
